
import { sitemapApi } from '@/api/sitemap';

export class SitemapGenerator {
  private static instance: SitemapGenerator;
  private lastGenerated: Date | null = null;
  private cachedSitemap: string | null = null;
  private cacheExpiry = 1000 * 60 * 60; // 1 hour cache

  public static getInstance(): SitemapGenerator {
    if (!SitemapGenerator.instance) {
      SitemapGenerator.instance = new SitemapGenerator();
    }
    return SitemapGenerator.instance;
  }

  /**
   * Generate sitemap with caching
   */
  public async generateSitemap(forceRefresh = false): Promise<string> {
    const now = new Date();
    
    // Return cached version if still valid
    if (
      !forceRefresh &&
      this.cachedSitemap &&
      this.lastGenerated &&
      (now.getTime() - this.lastGenerated.getTime()) < this.cacheExpiry
    ) {
      return this.cachedSitemap;
    }

    try {
      console.log('Generating fresh sitemap...');
      const sitemap = await sitemapApi.generateXmlSitemap();
      
      this.cachedSitemap = sitemap;
      this.lastGenerated = now;
      
      return sitemap;
    } catch (error) {
      console.error('Error generating sitemap:', error);
      
      // Return cached version if available, otherwise return basic sitemap
      if (this.cachedSitemap) {
        return this.cachedSitemap;
      }
      
      return this.generateBasicSitemap();
    }
  }

  /**
   * Generate a basic sitemap with essential pages only
   */
  private generateBasicSitemap(): string {
    const baseUrl = 'https://app.recut.art';
    const today = new Date().toISOString().split('T')[0];
    
    const basicUrls = [
      { loc: `${baseUrl}/`, priority: 1.0, changefreq: 'daily' },
      { loc: `${baseUrl}/channels`, priority: 0.9, changefreq: 'weekly' },
      { loc: `${baseUrl}/clips`, priority: 0.9, changefreq: 'weekly' },
      { loc: `${baseUrl}/playlists`, priority: 0.9, changefreq: 'weekly' },
      { loc: `${baseUrl}/derivatives`, priority: 0.8, changefreq: 'daily' },
      { loc: `${baseUrl}/creators`, priority: 0.7, changefreq: 'weekly' },
      { loc: `${baseUrl}/LamourCestSurcote`, priority: 0.9, changefreq: 'weekly' }
    ];

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    basicUrls.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${url.loc}</loc>\n`;
      xml += `    <lastmod>${today}</lastmod>\n`;
      xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      xml += `    <priority>${url.priority}</priority>\n`;
      xml += '  </url>\n';
    });
    
    xml += '</urlset>';
    
    return xml;
  }

  /**
   * Save sitemap to public directory
   */
  public async saveSitemapToPublic(): Promise<boolean> {
    try {
      const sitemap = await this.generateSitemap();
      
      // In a real implementation, you would save this to the public directory
      // For now, we'll just log it and return true
      console.log('Generated sitemap:', sitemap.substring(0, 200) + '...');
      
      return true;
    } catch (error) {
      console.error('Error saving sitemap:', error);
      return false;
    }
  }
}

export const sitemapGenerator = SitemapGenerator.getInstance();
